import { useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import Multiselect from '@rio-cloud/rio-uikit/Multiselect';

import type { Chat } from '../../services/chatAdministrationApi';
import { useAppDispatch } from '../../configuration/setup/hooks';
import { fleetGroupFilterSelectionChanged, useSelectedFleetGroupTagIds } from './fleetGroupFilterSlice';
import { getAvailableFleetGroupTags } from './fleetGroupFilter';
import { useGetFleetGroupTagsQuery } from '../../services/tagsApi';
import { useUserAccount } from '../../configuration/login/loginSlice';

type FleetGroupFilterProps = {
    chats: Chat[];
    className?: string;
};

export const FleetGroupFilter = ({ chats, className = '' }: FleetGroupFilterProps) => {
    const id = useId();
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const selectedTagIds = useSelectedFleetGroupTagIds();
    const userAccountId = useUserAccount();
    const { data: fleetGroupTags = [] } = useGetFleetGroupTagsQuery(userAccountId, {
        skip: !userAccountId,
    });

    const options: SelectOption[] = useMemo(
        () =>
            getAvailableFleetGroupTags(chats, fleetGroupTags).map(tag => ({
                id: tag.id,
                label: tag.name,
            })),
        [chats, fleetGroupTags]
    );

    if (options.length === 0) {
        return null;
    }

    return (
        <div className={className}>
            <Multiselect
                id={`fleet-group-filter-${id}`}
                value={selectedTagIds}
                options={options}
                placeholder={intl.formatMessage({ id: 'intl-msg:chat.fleetGroups.placeholder' })}
                inputAddon='rioglyph rioglyph-filter'
                useFilter
                counterMessage={{
                    one: intl.formatMessage({ id: 'intl-msg:chat.fleetGroups.counter.one' }),
                    many: intl.formatMessage({ id: 'intl-msg:chat.fleetGroups.counter.many' }),
                }}
                noItemMessage={intl.formatMessage({ id: 'intl-msg:chat.fleetGroups.noItems' })}
                onChange={selectedIds => dispatch(fleetGroupFilterSelectionChanged(selectedIds))}
            />
        </div>
    );
};
